/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import {
  EuiBadge,
  EuiBadgeGroup,
  EuiFlexGroup,
  EuiFlexItem,
  EuiIcon,
  EuiLink,
  EuiLoadingSpinner,
  EuiPanel,
  EuiStat,
  EuiText,
  EuiToolTip,
  useEuiTheme,
} from "@inscopix/ideas-eui";
import moment from "moment";
import { filesize } from "utils/filesize";
import { ContainerImageStatus } from "types/constants";
import { ButtonContainerInfo } from "./ButtonContainerInfo";
import { ButtonContainerActions } from "./ButtonContainerActions";
import { ContainerDjango } from "./ContainerBrowser.api";
import { Tag } from "./Tag";
import { ModalDeleteContainer } from "./ModalDeleteContainer";
import { ModalDownloadContainer } from "./ModalDownloadContainer";
import { useState } from "react";
import { useRouteMapContext } from "providers/RouteMapProvider/RouteMapProvider";

const getStyles = (euiTheme: ReturnType<typeof useEuiTheme>["euiTheme"]) => ({
  name: css`
    font-size: ${euiTheme.size.l};
  `,
  linkText: css`
    word-break: break-word;
  `,
});

interface ContainerCardProps extends ContainerDjango {
  onClick: () => void;
}

export const ContainerUploadingBadge = () => (
  <EuiToolTip
    content="This container image is being uploaded and will be available soon"
    position="bottom"
  >
    <EuiBadge color="hollow" title="">
      <EuiFlexGroup gutterSize="xs" alignItems="center">
        <EuiFlexItem grow={false}>
          <EuiLoadingSpinner size="s" />
        </EuiFlexItem>
        <EuiFlexItem>UPLOADING</EuiFlexItem>
      </EuiFlexGroup>
    </EuiBadge>
  </EuiToolTip>
);

export const ContainerCard = ({
  id,
  name,
  label,
  date_created,
  date_updated,
  description,
  full_name,
  user,
  tenant,
  size,
  tags,
  tools,
  status,
  onClick,
}: ContainerCardProps) => {
  const routeMap = useRouteMapContext((s) => s.routeMap);
  const { euiTheme } = useEuiTheme();
  const styles = getStyles(euiTheme);
  const [modal, setModal] = useState<"delete" | "download">();

  return (
    <>
      {(() => {
        const closeModal = () => setModal(undefined);
        switch (modal) {
          case "delete":
            return (
              <ModalDeleteContainer
                id={id}
                tools={tools}
                onClose={closeModal}
              />
            );
          case "download":
            return (
              <ModalDownloadContainer
                name={name}
                label={label}
                tenant={tenant}
                onClose={closeModal}
              />
            );
          case undefined:
            return null;
        }
      })()}

      <EuiPanel hasBorder>
        <EuiFlexGroup direction="column">
          <EuiFlexGroup
            alignItems="flexStart"
            justifyContent="spaceBetween"
            responsive={false}
            gutterSize="none"
          >
            <EuiFlexItem
              grow={false}
              css={{
                marginRight: "10px",
                alignSelf: "flex-start",
              }}
            >
              <EuiIcon type="package" size="l" />
            </EuiFlexItem>

            <EuiFlexItem grow>
              <EuiFlexGroup direction="column" gutterSize="none">
                <EuiFlexItem>
                  <EuiFlexGroup
                    direction="row"
                    gutterSize="s"
                    alignItems="center"
                  >
                    <EuiFlexItem grow={false}>
                      <EuiText css={styles.linkText}>
                        <EuiLink
                          css={styles.name}
                          target="_blank"
                          onClick={onClick}
                        >
                          {full_name}
                        </EuiLink>
                      </EuiText>
                    </EuiFlexItem>
                    {status === ContainerImageStatus.UPLOADING && (
                      <EuiFlexItem grow={false}>
                        <ContainerUploadingBadge />
                      </EuiFlexItem>
                    )}
                  </EuiFlexGroup>
                </EuiFlexItem>

                <EuiFlexItem>
                  <EuiText
                    size="s"
                    css={{ lineHeight: "15px" }}
                    color="subdued"
                  >
                    updated {moment(date_updated).fromNow()}
                  </EuiText>
                </EuiFlexItem>
              </EuiFlexGroup>
            </EuiFlexItem>

            <EuiFlexItem grow={false}>
              <ButtonContainerActions
                tenantId={tenant?.id}
                onDelete={() => setModal("delete")}
                onDownload={() => setModal("download")}
                containerImageStatus={status}
              />
            </EuiFlexItem>
          </EuiFlexGroup>

          {description !== null && description !== "" && (
            <EuiFlexItem>
              <EuiText>{description}</EuiText>
            </EuiFlexItem>
          )}

          {tags !== null && tags.length > 0 && (
            <EuiFlexItem grow={false}>
              <EuiBadgeGroup>
                {tags.map((tag) => (
                  <Tag key={tag} tag={tag} />
                ))}
              </EuiBadgeGroup>
            </EuiFlexItem>
          )}

          <EuiFlexGroup responsive={false} alignItems="center" gutterSize="m">
            <EuiFlexItem>
              <EuiFlexGroup alignItems="flexStart" responsive={false}>
                <EuiFlexItem grow={false}>
                  <EuiStat
                    titleSize="xs"
                    title={filesize(size)}
                    description="Storage"
                    reverse
                  />
                </EuiFlexItem>
              </EuiFlexGroup>
            </EuiFlexItem>

            {tenant && (
              <EuiFlexItem grow={false}>
                <EuiText>
                  <EuiLink
                    to={
                      routeMap["ORGANIZATION"].dynamicPath({
                        tenantKey: tenant.key,
                      }).path
                    }
                  >
                    {tenant.name}
                  </EuiLink>
                </EuiText>
              </EuiFlexItem>
            )}

            <EuiFlexItem grow={false}>
              <ButtonContainerInfo
                dateCreated={date_created}
                dateUpdated={date_updated}
                user={user?.id ?? null}
                anchorPosition="leftDown"
                repositionOnScroll
              />
            </EuiFlexItem>
          </EuiFlexGroup>
        </EuiFlexGroup>
      </EuiPanel>
    </>
  );
};
